import i18n from 'i18next';

import { scopeToLabelText } from 'new-components/ScopeResourceSection/helpers/constants';

/**
 * Parses a scope code (e.g. "o123-site:w", "o{orgId}-site:w", "o:r") and returns the group code and permission type.
 * Scope format: (o{orgId}-)?groupCode:r|w or o123-groupCode:r|w
 */
// Use \d+[a-zA-Z_]* instead of \d+\w* to avoid ReDoS: \w includes \d, causing
// overlapping quantifiers and catastrophic backtracking on malicious input.
const SCOPE_PATTERN = /^(o(?:\{\w+\}|\d+[a-zA-Z_]*)-)?(.*?):([rw])$/;

function parseScopeCode(
  scopeCode: string,
): { groupCode: string; type: 'r' | 'w' } | null {
  const match = SCOPE_PATTERN.exec(scopeCode);
  if (!match) return null;
  const groupCode = match[2];
  const type = match[3] as 'r' | 'w';
  return groupCode ? { groupCode, type } : null;
}

/**
 * Converts a single scope code to a human-readable description (e.g. "Sites: Write").
 */
export function scopeCodeToDescription(scopeCode: string): string {
  const parsed = parseScopeCode(scopeCode);
  if (!parsed) return scopeCode;

  const label = scopeToLabelText(parsed.groupCode);
  const permissionLabel =
    parsed.type === 'r' ? i18n.t('Read') : i18n.t('Write');
  return label ? `${label}: ${permissionLabel}` : scopeCode;
}

/**
 * Converts an array of scope codes to human-readable descriptions.
 * Deduplicates by description (e.g. o123-site:r and o123-site:w become "Sites: Read", "Sites: Write").
 */
export function scopeCodesToDescriptions(scopeCodes: string[]): string[] {
  if (!scopeCodes?.length) return [];

  const descriptions = scopeCodes.map(scopeCodeToDescription);
  return [...new Set(descriptions)];
}
